/**
 * /admin/tickets/:ticketId — detail view + chat + status management for admin_global.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, LifeBuoy } from 'lucide-react';
import { watchTicket } from '@/lib/firestore';
import { callUpdateTicketStatus } from '@/lib/functions';
import type { Ticket, TicketStatus } from '@/types/models';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { TicketChat } from '@/components/tickets/TicketChat';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';

const STATUSES: { value: TicketStatus; labelKey: string }[] = [
  { value: 'open',        labelKey: 'support.status.open' },
  { value: 'in_progress', labelKey: 'support.status.in_progress' },
  { value: 'closed',      labelKey: 'support.status.closed' },
];

function ticketBadgeVariant(status: string) {
  if (status === 'open') return 'warning' as const;
  if (status === 'in_progress') return 'info' as const;
  return 'neutral' as const;
}

export default function AdminTicketDetail() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { t, locale } = useT();

  const [ticket, setTicket] = useState<Ticket | null | undefined>(undefined);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusError, setStatusError] = useState('');

  useEffect(() => {
    if (!ticketId) return;
    return watchTicket(ticketId, setTicket);
  }, [ticketId]);

  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (!ticketId || !ticket || ticket.status === newStatus) return;
    setUpdatingStatus(true);
    setStatusError('');
    try {
      await callUpdateTicketStatus({ ticketId, status: newStatus });
    } catch (err: unknown) {
      setStatusError(err instanceof Error ? err.message : 'Error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (ticket === undefined) return <Loader fullscreen={false} />;
  if (!ticket) return (
    <div className="space-y-4">
      <button onClick={() => navigate('/admin/tickets')} className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 dark:text-muted-foreground">
        <ArrowLeft className="h-4 w-4" />{t('admin.tickets.detail.back')}
      </button>
      <p className="text-sm text-ink-500">—</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate('/admin/tickets')}
        className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 dark:text-muted-foreground dark:hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('admin.tickets.detail.back')}
      </button>

      {/* Header card */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-soft dark:border-border dark:bg-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-primary/10 dark:text-primary">
              <LifeBuoy className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl text-ink-900 dark:text-white">{ticket.subject}</h1>
              <p className="mt-0.5 text-sm text-ink-500 dark:text-muted-foreground">
                {ticket.orgName} · {t(`support.category.${ticket.category}` as any)}
              </p>
            </div>
          </div>

          {/* Status selector */}
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-500 dark:text-muted-foreground">
                {t('admin.tickets.status.change')}
              </span>
              <select
                value={ticket.status}
                onChange={e => handleStatusChange(e.target.value as TicketStatus)}
                disabled={updatingStatus}
                className="rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink-900 focus:outline-hidden focus:ring-2 focus:ring-brand-500 disabled:opacity-50 dark:border-border dark:bg-card dark:text-foreground"
              >
                {STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{t(s.labelKey as any)}</option>
                ))}
              </select>
            </div>
            <Badge variant={ticketBadgeVariant(ticket.status)}>
              {t(`support.status.${ticket.status}` as any)}
            </Badge>
            {statusError && <p className="text-xs text-red-600">{statusError}</p>}
          </div>
        </div>

        <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-500 dark:text-muted-foreground">
          <div className="flex gap-1">
            <dt className="font-medium">{t('support.detail.created_by')}:</dt>
            <dd>{ticket.createdByName || ticket.createdByEmail}</dd>
          </div>
          <div className="flex gap-1">
            <dt className="font-medium">{t('support.detail.opened')}:</dt>
            <dd>{fmtDate(ticket.createdAt, locale)}</dd>
          </div>
          <div className="flex gap-1">
            <dt className="font-medium"># messages:</dt>
            <dd>{ticket.messageCount}</dd>
          </div>
        </dl>
      </div>

      {/* Chat */}
      <TicketChat ticketId={ticket.id} status={ticket.status} />
    </div>
  );
}
